import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import SyncStatusBanner from "../../components/client/SyncStatusBanner";
import { useAuth } from "../../context/AuthContext";
import { clientApi } from "../../api";
import { getToken } from "../../utils/authStorage";
import { formatDateAU } from "../../utils/formatDate";

const PRESETS = [7, 30, 60];

export default function ExpiringReport() {
  const { user } = useAuth();
  const isAdmin = user?.clientRole === "admin";

  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [cache, setCache] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [venue, setVenue] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const d = await clientApi.getExpiring(days);
      setRows(d.members || []);
      setCache(d.cache || null);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { clientApi.getMe().then((me) => setVenue(me?.venue || null)).catch(() => {}); }, []);

  async function sendReminders() {
    if (!rows.length) return;
    if (!window.confirm(`Send renewal reminder emails to ${rows.length} member(s)?`)) return;
    setSending(true); setResult(null); setError("");
    try {
      const r = await clientApi.sendExpiryReminders({ days, members: rows });
      setResult(r);
    } catch (err) { setError(err.message); }
    setSending(false);
  }

  async function downloadCsv() {
    setError("");
    const token = getToken("client");
    const res = await fetch(clientApi.expiringCsvUrl(days), { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { setError("CSV download failed. Please try again."); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `expiring-${days}d.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Memberships Expiring</h1>
          <p className="page-subtitle">Portal-managed memberships expiring in the next N days. For physical card expiry see <Link to="/client/reports/cards-expiring">Cards Expiring</Link>.</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="btn btn-outline" onClick={downloadCsv}>Download CSV</button>
          {isAdmin && <button className="btn btn-primary" onClick={sendReminders} disabled={sending || !rows.length}>{sending ? "Sending…" : `Send ${rows.length} reminders`}</button>}
        </div>
      </div>

      <SyncStatusBanner cache={cache} provider={venue?.provider_type} />

      <div style={{ display: "flex", gap: "0.5rem", margin: "1rem 0", alignItems: "center" }}>
        {PRESETS.map((d) => (
          <button key={d} className={`btn btn-sm ${days === d ? "btn-primary" : "btn-outline"}`} aria-pressed={days === d} onClick={() => setDays(d)}>Next {d} days</button>
        ))}
        <input
          type="number"
          aria-label="Days ahead"
          value={days}
          onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 30))}
          style={{ width: 90, padding: "0.4rem 0.6rem", border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)", borderRadius: "var(--radius-sm)" }}
        />
      </div>

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {result && (
        <div className="toast toast-success" style={{ marginBottom: "1rem" }}>
          Sent {result.sent} · Skipped (no email) {result.skipped} · Failed {result.failed}
        </div>
      )}

      {loading ? <div className="loading-state"><div className="spinner" /></div> : rows.length ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Member #</th><th>Name</th><th>Email</th><th>Mobile</th><th>Expires</th></tr></thead>
            <tbody>
              {rows.map((m, i) => (
                <tr key={m.membernumber || i}>
                  <td><Link to={`/client/members/${m.membernumber}`}><code>#{m.membernumber}</code></Link></td>
                  <td>{m.firstname} {m.surname}</td>
                  <td style={{ fontSize: "0.85rem", color: m.email ? "var(--text)" : "var(--text-muted)" }}>{m.email || "no email"}</td>
                  <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{m.mobile || "—"}</td>
                  <td>{formatDate(m.dateexpiry)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <h3>No members expiring in the next {days} days</h3>
          <p className="muted">Try widening the range, or check the sync status above.</p>
        </div>
      )}
    </ClientLayout>
  );
}

function formatDate(v) {
  return formatDateAU(v);
}
